import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  HostListener,
  ViewChildren,
  QueryList,
} from '@angular/core';
import {
  ChapterFormResources,
  ChapterStatusEnum,
  ChapterTypesEnum,
  DefaultChapterFormResources,
} from './chapter-form.component.resources';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { distinctUntilChanged, startWith, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { selectSubChaptersById } from '../../../store/admin/admin.reducer';
import { Store } from '@ngrx/store';
import {
  AddSubChapterAction,
  FetchSubChaptersAction,
  UpdateChapterAction,
  UpdateChapterSuccessAction,
} from '../../../store/admin/admin.actions';
import {
  BookStatus,
  ChapterType,
  IChapter,
  ICreateChapterRequest,
} from '../../../models/admin';
import { MediaDeliveryService } from '../../../services/mediaDelivery.services';
import { IUploadFileResponse } from '../../../models/mediaDelivery';
import { SnackBarService } from '../../../services/snackbar.service';
import { AdminService } from '../../../services/admin.services';
import { getRawUrlHelper } from '../../../_helpers/common.functions';

import { extractFileName } from '../../../_helpers/common.functions';
import { Actions, ofType } from '@ngrx/effects';

interface AudioItem {
  url: string;
  blobUrl?: string;
}
@Component({
  selector: 'app-chapter-form',
  standalone: false,
  templateUrl: './chapter-form.component.html',
  styleUrl: './chapter-form.component.scss',
})
export class ChapterFormComponentCore implements OnInit, OnDestroy {
  @Input() resources: Partial<ChapterFormResources> = {};
  finalResources!: ChapterFormResources;
  @ViewChild('accessRightsWrapper') accessRightsWrapper!: ElementRef;
  audioItems: AudioItem[] = [];
  private _subscriptions: Subscription[] = [];
  isAddMode: boolean = false;
  accessRightsDropdownOpen: boolean = false;
  availableAccessRights: string[] = ['chapter.full'];
  chapterForm!: FormGroup;
  initialFormValues: any = {}; // Store Initial Values
  isFormChanged = false;
  entityMode: 'chapter' | 'subchapter' = 'subchapter';
  draggedAudioIndex: number | null = null;
  dragOverAudioIndex: number | null = null;
  dragEnabledIndex: number | null = null;

  // Dropdown Options
  chapterTypeOptions = Object.values(ChapterTypesEnum);
  chapterStatusOptions = Object.values(ChapterStatusEnum);

  chapterId!: string;
  subChapterId!: string;
  bookId!: string;
  chapter: IChapter | null = null;

  audioError: string = '';
  imageError: string = '';
  chapterIconError: string = '';

  imagePreviewUrl: string | ArrayBuffer | null = null;
  chapterIconPreviewUrl: string | ArrayBuffer | null = null;
  thumbnailFileName: string | null = null;
  chapterIconFileName: string | null = null;

  get isDraftState() {
    return (
      this.chapter?.Status === BookStatus.Draft &&
      this.chapterForm.get('Status')?.value === BookStatus.Draft
    );
  }

  private readonly controlsToToggle = [
    'Title',
    'Description',
    'GroupName',
    'AccessRights',
  ];

  @ViewChildren('audioPlayer')
  audioPlayers!: QueryList<ElementRef<HTMLAudioElement>>;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private store: Store,
    private mediaService: MediaDeliveryService,
    private snackBar: SnackBarService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private adminService: AdminService,
    private actions$: Actions,
  ) {}

  // Update enable/disable state of controls
  private updateControlStates(): void {
    const enableControls = this.isAddMode || this.isDraftState;

    this.chapterForm
      .get('Status')
      ?.[this.isAddMode ? 'disable' : 'enable']({ emitEvent: false });
    this.controlsToToggle.forEach(
      (control) =>
        this.chapterForm
          .get(control)
          ?.[enableControls ? 'enable' : 'disable']({ emitEvent: false }),
    );
    // Chapter/sub-chapter types are fixed and can never be changed from the UI.
    this.chapterForm.get('Type')?.disable({ emitEvent: false });
  }

  applyDisableStyle(controlName: string): boolean {
    if (controlName === 'Type') {
      return true;
    }
    return controlName === 'Status'
      ? this.isAddMode
      : !this.isAddMode && !this.isDraftState;
  }

  onSubmit() {
    const changedFields: any = this.getChangedFields();

    if (this.entityMode === 'chapter') {
      if (this.isAddMode) {
        const req: ICreateChapterRequest = {
          GroupName: changedFields.GroupName || null,
          Title: changedFields.Title,
          Type: ChapterType.Chat,
          Description: changedFields.Description || null,
          ThumbnailUrl: changedFields.ThumbnailUrl || null,
          IconUrl: changedFields.IconUrl || null,
          AudioUrl: changedFields.AudioFileUrl || null,
          AccessRights: changedFields.AccessRights,
          SubChapters: [],
        };
        this.adminService
          .postEditorChapter(this.bookId, req, true)
          .subscribe(() => this.OnCancelBtnClick());
      } else if (this.chapterForm.valid && this.isFormChanged) {
        if (!this.chapterForm.get('Status')?.dirty) {
          delete changedFields.Status;
        }
        this.store.dispatch(
          UpdateChapterAction({
            chapterId: this.chapterId,
            updatedFields: changedFields,
          }),
        );
      }
      return;
    }

    if (this.isAddMode) {
      const newSubChapter: any = {
        groupName: changedFields.GroupName || null,
        type: ChapterType[changedFields.Type],
        title: changedFields.Title,
        description: changedFields.Description || null,
        accessRights: changedFields.AccessRights,
        audioFileUrl: changedFields.AudioFileUrl || null,
        thumnailUrl: changedFields.ThumbnailUrl || null,
        IconUrl: changedFields.IconUrl || null,
      };
      this.store.dispatch(
        AddSubChapterAction({
          chapterId: this.chapterId,
          subChapterInfo: newSubChapter,
        }),
      );
      return;
    }
    if (this.chapterForm.valid && this.isFormChanged) {
      this.store.dispatch(
        UpdateChapterAction({
          chapterId: this.subChapterId,
          updatedFields: changedFields,
          parentChapterId: this.chapterId,
        }),
      );
    }
  }

  toggleAccessRightsDropdown(event: Event): void {
    event.stopPropagation();
    this.accessRightsDropdownOpen = !this.accessRightsDropdownOpen;
  }

  onAudioPlay(currentAudio: HTMLAudioElement): void {
    this.audioPlayers.forEach((player) => {
      const audio = player.nativeElement;

      if (audio !== currentAudio) {
        audio.pause();
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.accessRightsDropdownOpen &&
      this.accessRightsWrapper &&
      !this.accessRightsWrapper.nativeElement.contains(event.target)
    ) {
      this.accessRightsDropdownOpen = false;
    }
  }

  removeThumbnail(): void {
    this.imagePreviewUrl = null;
    this.thumbnailFileName = null;
    this.imageError = '';
    const control = this.chapterForm.get('ThumbnailUrl');
    control?.setValue('');
    control?.markAsDirty();
  }

  removeChapterIcon(): void {
    this.chapterIconPreviewUrl = null;
    this.chapterIconFileName = null;
    this.chapterIconError = '';
    const control = this.chapterForm.get('IconUrl');
    control?.setValue('');
    control?.markAsDirty();
  }
  getAudioFileName(url: string): string {
    return extractFileName(url) || url;
  }

  moveAudioItem(index: number, direction: -1 | 1): void {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= this.audioItems.length) return;
    const items = [...this.audioItems];
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    this.audioItems = items;
    this.syncAudioFileUrlControl();
  }

  removeAudioItem(index: number): void {
    const removed = this.audioItems[index];
    if (removed?.blobUrl) {
      URL.revokeObjectURL(removed.blobUrl);
    }
    this.audioItems = this.audioItems.filter((_, i) => i !== index);
    this.syncAudioFileUrlControl();
  }

  private syncAudioFileUrlControl(): void {
    const joined = this.audioItems.map((i) => i.url).join(',');
    const control = this.chapterForm.get('AudioFileUrl');
    control?.setValue(joined);
    control?.markAsDirty();
  }

  fetchAndSetAudioItem(item: AudioItem) {
    this.mediaService.downloadMediaFile(getRawUrlHelper(item.url)).subscribe(
      (blob: Blob) => {
        item.blobUrl = URL.createObjectURL(blob);
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Failed to fetch audio', error);
      },
    );
  }

  getFormTitle(): string {
    if (this.entityMode === 'subchapter') {
      return this.isAddMode
        ? (this.finalResources as any).addSubChapterTitle || 'Add Sub Chapter'
        : (this.finalResources as any).updateSubChapterTitle ||
            'Update Sub Chapter';
    }
    return this.isAddMode
      ? this.finalResources.addChapterTitle
      : this.finalResources.udpateChapterTitle;
  }

  onAudioHandleMouseDown(index: number): void {
    if (this.isDraftState) {
      this.dragEnabledIndex = index;
    }
  }

  onAudioHandleMouseUp(): void {
    // If a drag never started (e.g. plain click), clear it.
    if (this.draggedAudioIndex === null) {
      this.dragEnabledIndex = null;
    }
  }

  onAudioDragStart(event: DragEvent, index: number): void {
    if (this.isDraftState) {
      this.draggedAudioIndex = index;
      event.dataTransfer?.setData('text/plain', index.toString());
      if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
    }
  }

  onAudioDragEnd(): void {
    if (this.isDraftState) {
      this.draggedAudioIndex = null;
      this.dragOverAudioIndex = null;
      this.dragEnabledIndex = null;
    }
  }

  onAudioDragOver(event: DragEvent, index: number): void {
    if (this.isDraftState) {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
      this.dragOverAudioIndex = index;
    }
  }

  onAudioDragLeave(): void {
    if (this.isDraftState) {
      this.dragOverAudioIndex = null;
    }
  }

  onAudioDrop(event: DragEvent, index: number): void {
    event.preventDefault();
    if (this.draggedAudioIndex === null || this.draggedAudioIndex === index) {
      this.dragOverAudioIndex = null;
      return;
    }
    const items = [...this.audioItems];
    const [moved] = items.splice(this.draggedAudioIndex, 1);
    items.splice(index, 0, moved);
    this.audioItems = items;
    this.syncAudioFileUrlControl();
    this.draggedAudioIndex = null;
    this.dragOverAudioIndex = null;
  }

  OnCancelBtnClick() {
    if (this.entityMode === 'subchapter') {
      this.router.navigate(
        [
          'admin/books/',
          this.bookId,
          'chapters',
          this.chapterId,
          'subchapters',
        ],
        { queryParams: { tab: 'chapters' }, queryParamsHandling: 'merge' },
      );
    } else {
      this.router.navigate(['admin/books/', this.bookId, 'chapters'], {
        queryParams: { tab: 'chapters' },
        queryParamsHandling: 'merge',
      });
    }
  }

  triggerFileUpload() {
    const fileInput = document.getElementById(
      'thumbnail-upload',
    ) as HTMLInputElement;
    fileInput.click();
  }
  onAccessRightToggle(event: Event, right: string): void {
    const input = event.target as HTMLInputElement;
    const checked = input.checked;

    const control = this.chapterForm.get('AccessRights');
    let rights: string[] = control?.value || [];

    rights = [...rights]; // Copy to trigger change detection

    if (checked && !rights.includes(right)) {
      rights.push(right);
    } else if (!checked) {
      rights = rights.filter((r: string) => r !== right);
    }

    control?.setValue(rights);
    control?.markAsDirty();
    control?.updateValueAndValidity();
  }

  isRightSelected(right: string): boolean {
    return this.chapterForm.get('AccessRights')?.value?.includes(right);
  }

  getSelectedAccessRightsLabel(): string {
    const selected = this.chapterForm.get('AccessRights')?.value || [];
    return selected.length ? selected.join(', ') : '';
  }

  onFileSelected(
    event: Event,
    fileType: 'Audio' | 'ThumbnailImage' | 'ChapterIcon',
  ) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    // Handle Audio File upload
    if (fileType === 'Audio') {
      const files = Array.from(input.files);
      let hadInvalid = false;

      files.forEach((file) => {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (!['mp3', 'wav'].includes(fileExtension!)) {
          hadInvalid = true;
          return;
        }
        this.uploadFile(file, 'Audio');
      });

      this.audioError = hadInvalid
        ? 'Some files were skipped. Only .mp3 and .wav are allowed.'
        : '';
      input.value = '';
    } else {
      const file = input.files[0];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      let imageError = null;

      if (!['jpeg', 'jpg', 'png'].includes(fileExtension!)) {
        imageError =
          'Invalid file type. Only .jpeg, .jpg, and .png are allowed.';
      }

      // Handle Thumbnail image upload
      if (fileType == 'ThumbnailImage') {
        if (imageError) {
          this.imageError = imageError;
        } else {
          this.imageError = '';
          this.uploadFile(file, fileType);
        }
        input.value = '';
      } else if (fileType == 'ChapterIcon') {
        // Handle Chapter Icon upload

        if (imageError) {
          this.chapterIconError = imageError;
        } else {
          this.chapterIconError = '';
          this.uploadFile(file, fileType);
        }
        input.value = '';
      }
    }
  }

  uploadFile(file: File, fileType: 'Audio' | 'ThumbnailImage' | 'ChapterIcon') {
    this.mediaService.chapterMediaUpload(file).subscribe({
      next: (response: IUploadFileResponse) => {
        const successMsg = `${fileType} uploaded successfully.`;
        const rawUrl = getRawUrlHelper(response.Value.FileUrl);

        switch (fileType) {
          case 'Audio':
            const newItem: AudioItem = { url: rawUrl };
            this.audioItems = [...this.audioItems, newItem];
            this.syncAudioFileUrlControl();
            this.fetchAndSetAudioItem(newItem);
            break;
          case 'ThumbnailImage':
            this.chapterForm.patchValue({ ThumbnailUrl: rawUrl });
            this.thumbnailFileName = extractFileName(rawUrl);
            this.downloadAndShowImage(rawUrl, fileType);
            break;
          case 'ChapterIcon':
            this.chapterForm.patchValue({ IconUrl: rawUrl });
            this.chapterIconFileName = extractFileName(rawUrl);
            this.downloadAndShowImage(rawUrl, fileType);
            break;
        }

        this.snackBar.showSnackBar('success', successMsg);
      },
      error: (error) => {
        const errorMsg = `Error uploading ${fileType}`;
        if (fileType === 'Audio') {
          this.audioError = 'Failed to upload audio. Please try again.';
        } else if (fileType == 'ThumbnailImage') {
          this.imageError = 'Failed to upload image. Please try again.';
        } else if (fileType == 'ChapterIcon') {
          this.chapterIconError =
            'Failed to upload chapter icon. Please try again.';
        }
        this.snackBar.showSnackBar('error', errorMsg);
        console.error(error);
      },
    });
  }

  private loadSubChapterData(): void {
    this._subscriptions.push(
      this.store
        .select(selectSubChaptersById(this.chapterId!))
        .pipe(distinctUntilChanged())
        .subscribe((subChapters) => {
          if (!subChapters && this.chapterId) {
            this.store.dispatch(
              FetchSubChaptersAction({ chapterId: this.chapterId }),
            );
            return;
          }
          if (subChapters) {
            this.applyLoadedChapter(
              subChapters.find((s) => s.ChapterId === this.subChapterId) ||
                null,
            );
          }
        }),
    );
  }

  private loadChapterData(): void {
    this.adminService
      .getEditorChapters(this.bookId, true)
      .subscribe((response) => {
        const chapter = response.Value.find(
          (c: IChapter) => c.ChapterId === this.chapterId,
        );
        if (chapter) {
          this.applyLoadedChapter(chapter);
        } else {
          this.snackBar.showSnackBar('error', 'Chapter not found');
          this.router.navigate(['/admin/books', this.bookId]);
        }
      });
  }

  private applyLoadedChapter(chapter: IChapter | null): void {
    this.audioItems = [];
    this.chapter = chapter;
    this.cdr.detectChanges();

    if (!chapter) return;

    const chapterTypeKey = ChapterType[chapter.Type];
    const chapterTypeValue = ChapterTypesEnum[chapterTypeKey]?.value || '';

    this.isFormChanged = false;

    const rawChapterIconUrl = chapter.IconUrl
      ? getRawUrlHelper(chapter.IconUrl)
      : '';
    const rawThumbnailUrl = chapter.ThumbnailUrl
      ? getRawUrlHelper(chapter.ThumbnailUrl)
      : '';
    const rawAudioFileUrl = chapter.AudioFileUrl
      ? chapter.AudioFileUrl.split(',')
          .map((u) => getRawUrlHelper(u.trim()))
          .filter((u) => u.length > 0)
          .join(',')
      : '';

    this.chapterForm.patchValue({
      Title: chapter.Title || '',
      Type: chapterTypeValue,
      Description: chapter.Description || '',
      Status: chapter.Status,
      ThumbnailUrl: rawThumbnailUrl,
      AudioFileUrl: rawAudioFileUrl,
      IconUrl: rawChapterIconUrl,
      GroupName: chapter.GroupName || '',
      AccessRights: chapter.AccessRights || [],
    });

    this.initialFormValues = this.chapterForm.getRawValue();
    this.isFormChanged = false;

    if (rawChapterIconUrl) {
      this.chapterIconFileName = extractFileName(rawChapterIconUrl);
      this.downloadAndShowImage(rawChapterIconUrl, 'ChapterIcon');
    }
    if (rawThumbnailUrl) {
      this.thumbnailFileName = extractFileName(rawThumbnailUrl);
      this.downloadAndShowImage(rawThumbnailUrl, 'ThumbnailImage');
    }
    if (rawAudioFileUrl) {
      this.audioItems = rawAudioFileUrl
        .split(',')
        .filter((u) => u.length > 0)
        .map((url) => ({ url }));
      this.audioItems.forEach((item) => this.fetchAndSetAudioItem(item));
    }
  }

  ngOnInit(): void {
    // Resources
    this.finalResources = { ...DefaultChapterFormResources, ...this.resources };

    this.chapterForm = this.fb.group({
      Title: ['', [Validators.required, Validators.minLength(3)]],
      Type: [null, Validators.required],
      Description: [''],
      Status: [ChapterStatusEnum.Draft.textValue, Validators.required],
      ThumbnailUrl: [''],
      IconUrl: [''],
      AudioFileUrl: [''],
      GroupName: [''],
      AccessRights: [[]],
    });

    this._subscriptions.push(
      this.chapterForm
        .get('Status')!
        .valueChanges.pipe(startWith(this.chapterForm.get('Status')!.value))
        .subscribe(() => {
          this.updateControlStates();
        }),
    );

    // Get route parameters
    this._subscriptions.push(
      this.route.paramMap.subscribe((params) => {
        this.bookId = params.get('bookId') || '';
        // const subChapterIdParam = params.get('subchapterId');
        const subChapterIdParam =
          params.get('subchapterId') ?? params.get('subChapterId');

        if (subChapterIdParam !== null) {
          // editAddSubChapters route: chapterId = parent chapter, subchapterId = the entity being edited
          this.entityMode = 'subchapter';
          this.chapterId = params.get('chapterId') || '';
          this.subChapterId = subChapterIdParam;
          this.isAddMode = this.subChapterId === 'add';
          this.loadSubChapterData();
        } else {
          // chapterAdd / chapterEdit routes
          this.entityMode = 'chapter';
          this.chapterId = params.get('chapterId') || '';
          this.isAddMode = !this.chapterId || this.chapterId === 'add';
          if (!this.isAddMode) {
            this.loadChapterData();
          }
        }
      }),
    );

    // Detect changes in form values
    this._subscriptions.push(
      this.chapterForm.valueChanges.subscribe(() => {
        this.isFormChanged = this.hasFormChanged();
        this.cdr.detectChanges();
      }),
    );

    // update the form after chapter update
    this._subscriptions.push(
      this.actions$
        .pipe(ofType(UpdateChapterSuccessAction))
        .subscribe((res) => {
          this.chapterForm.markAsPristine();
          this.initialFormValues = this.chapterForm.getRawValue();
          this.isFormChanged = false;
          this.applyLoadedChapter(res.response.Value);
        }),
    );
  }

  downloadAndShowImage(
    imageUrl: string,
    fileType: 'ThumbnailImage' | 'ChapterIcon',
  ) {
    const url = getRawUrlHelper(imageUrl);
    this.mediaService.downloadMediaFile(url).subscribe((res) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (fileType == 'ThumbnailImage') {
          this.imagePreviewUrl = reader.result;
        } else if (fileType == 'ChapterIcon') {
          this.chapterIconPreviewUrl = reader.result;
        }
        this.cdr.detectChanges();
      };
      reader.readAsDataURL(res);
    });
  }
  // Check If Form Has Been Changed
  private hasFormChanged(): boolean {
    // A dirty control counts as a change even when the value round-trips back
    // to its original (e.g. remove + re-add the same audio files).
    if (this.chapterForm.dirty) return true;

    const currentValues = this.chapterForm.getRawValue();

    return Object.keys(currentValues).some(
      (key) =>
        !this.valuesEqual(this.initialFormValues[key], currentValues[key]),
    );
  }

  // Order-sensitive value comparison shared by change detection and diffing.
  private valuesEqual(initialValue: any, currentValue: any): boolean {
    if (Array.isArray(initialValue) && Array.isArray(currentValue)) {
      if (initialValue.length !== currentValue.length) return false;
      // Compare by index: reordering AccessRights is a real change.
      return initialValue.every((val, i) => val === currentValue[i]);
    }
    return initialValue === currentValue;
  }

  // Gets Only Changed Fields
  private getChangedFields(): any {
    const currentValues = this.chapterForm.getRawValue();
    return Object.keys(currentValues).reduce((acc, key) => {
      const currentValue = currentValues[key];
      const initialValue = this.initialFormValues[key];

      // Include a field when its value differs OR when its control was
      // explicitly touched. The dirty check matters for cases such as removing
      // and re-adding the same audio files, where the final value equals the
      // initial one but the user did perform an edit.
      const isDirty = !!this.chapterForm.get(key)?.dirty;

      if (!this.valuesEqual(initialValue, currentValue) || isDirty) {
        // NOTE: never coerce '' to null here. The backend treats a null value
        // as "field not provided" and retains the previous value, so clearing
        // AudioFileUrl / ThumbnailUrl / IconUrl must be sent as an empty
        // string for the change to persist.
        acc[key] = currentValue;
      }
      return acc;
    }, {} as any);
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((sub) => sub.unsubscribe());
    this._subscriptions = [];
    this.audioItems.forEach((item) => {
      if (item.blobUrl) URL.revokeObjectURL(item.blobUrl);
    });
  }
}
